import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { exportIncomeTaxBankAccDetails, getIncomeTaxBankAccDetails } from "api/services/automation/income-tax";
import SearchContainer from "components/SearchContainer";
import { ColumnType } from "components/Table";
import sortHandler from "components/Table/SortHandler";
import useTitle from "hooks/useTitle";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { ResType } from "types";
import _ from "lodash";
import { getCurrentFormattedDateTime } from "utils";
import { snack } from "components/toast";
import { handleError } from "utils/handleError";
import excelIcon from "./../.././assets/images/128px-Microsoft_Office_Excel_(2019–2025).svg.png";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { accountHolderTypeMap, accountStatusMap, accountTypeMap, dropDownType, getStatusColor, getStatusText, yesNoMap } from "./constants";
import HorizontalTable from "components/Table/HorizontalTable";
import { hasActiveFilters } from "./Filters/components";
import { buildChips } from "./Filters/buildChips";
import useClientCategories from "hooks/useClientCategories";
import FilterEngine from "./Filters";
import clearFilters from "./../../assets/images/clear-filter.svg";




const IncomeTaxBankAccDeatils = () => {
  useTitle("ATOM Pro | Registered DSC's");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [remarksDialog, setRemarksDialog] = useState<{ open: boolean; text: string }>({
    open: false,
    text: "",
  });

  const [filters, setFilters] = useState<any>({
    accountType: null,
    status: null,
    clientCategory: null,
  });


  const [sort, setSort] = useState<any>({});

  const { data, isLoading }: ResType = useQuery(
    [
      "income-tax-bank-acc-details",
      {
        search: search,
        limit: pageCount,
        offset: page * pageCount,
        sort,
        ...filters
      },
    ],
    getIncomeTaxBankAccDetails,
    {
      onSuccess: (res) => {
        setTotalCount(res?.data?.count);
      },

    }
  );

  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber) return "--";

    const visibleChars = 4;

    if (accountNumber.length <= visibleChars * 2) {
      return accountNumber;
    }

    return (
      accountNumber.slice(0, visibleChars) +
      "X".repeat(accountNumber.length - visibleChars * 2) +
      accountNumber.slice(-visibleChars)
    );
  };


  let defaultColumns: Array<ColumnType> = [

    {
      key: "clientId",
      sort: true,
      active: false,
      // direction: SortDirection.ASC,
      default: true,
      title: (
        <Box display="flex" alignItems="center">
          {/* <PersonOutlineOutlinedIcon style={{ width: "20px", marginRight: "5px" }} />  */}
          Assessee Name (PAN)
        </Box>
      ),
      render: (row) => (
        <Typography
          sx={{
            maxWidth: 180,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={`${row?.credentials?.client?.displayName} (${row?.credentials?.panNumber})`}
        >
          {row?.credentials?.client?.displayName} (<span>{row?.credentials?.panNumber}</span>)
        </Typography>
      ),
    },
    {
      key: "bankName",
      title: "Bank (Branch)",
      sort: true,
      active: false,
      // direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Typography
            sx={{
              maxWidth: 150,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`${row?.bankName} (${row?.bankBrnchTxt || "--"})`}
          >{row?.bankName}(<span>{row?.bankBrnchTxt}</span>)</Typography>
        );
      },
    },
    {
      key: "accountHolderType",
      title: "A/c Holder Type",
      sort: true,
      active: false,
      // direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Typography variant="body1">{row?.accountHolderType ? accountHolderTypeMap[row?.accountHolderType] : "--"}</Typography>
        );
      },
    },
    {
      key: "accountType",
      title: "A/c Type",
      sort: true,
      active: false,
      // direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Typography variant="body1">{row?.accountType ? accountTypeMap[row?.accountType] : '--'}</Typography>
        );
      },
    },
    {
      key: "ifscCd",
      title: "IFSC",
      sort: true,
      active: false,
      // direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Typography variant="body1">{row?.ifscCd}</Typography>
        );
      },
    },
    {
      key: "bankAcctNum",
      title: "Account#",
      sort: true,
      active: false,
      // direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Typography variant="body1">{row?.bankAcctNum ? maskAccountNumber(row?.bankAcctNum) : '--'}</Typography>
        );
      },
    },
    {
      key: "evcFlag",
      title: "EVC",
      sort: true,
      active: false,
      // direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Typography variant="body1" sx={{ fontWeight: 'bold' }} color={row?.evcFlag === "Y" ? "green" : "error.main"}>{row?.evcFlag ? yesNoMap[row?.evcFlag] : "--"}</Typography>
        );
      },
    },
    {
      key: "refundFlag",
      title: "Refund",
      sort: true,
      active: false,
      // direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Typography variant="body1" sx={{ fontWeight: 'bold' }} color={row?.refundFlag === "Y" ? "green" : "error.main"}>{row?.refundFlag ? yesNoMap[row?.refundFlag] : "--"}</Typography>
        );
      },
    },
    {
      key: "status",
      title: "Status",
      sort: true,
      active: false,
      // direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Typography variant="body1" sx={{ fontWeight: 'bold' }} color={getStatusColor(row?.bankGroup)}>{getStatusText(row?.bankGroup)}</Typography>
        );
      },
    },
    {
      key: "errorCd",
      title: "Remarks",
      sort: true,
      active: false,
      default: true,
      // UPDATED: truncated text + eye icon opens full remarks in a dialog
      render: (row: any) => {
        const remarks = row?.errorCd;
        if (!remarks) return <Typography variant="body1">-</Typography>;
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography
              variant="body1"
              sx={{ maxWidth: 80, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              title={remarks}
            >
              {remarks}
            </Typography>
            <Tooltip title="View full remarks">
              <IconButton size="small" onClick={() => setRemarksDialog({ open: true, text: remarks })}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    }


  ];
  const [columns, setColumns] = useState(_.cloneDeep(defaultColumns));


  const { mutate: mutate1, isLoading: isExporting } = useMutation(exportIncomeTaxBankAccDetails, {
    onSuccess: (res: any) => {
      const arr = new Uint8Array(res.data?.data);
      const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const file = window.URL.createObjectURL(blob);
      const formattedDateTime = getCurrentFormattedDateTime();

      let link = document.createElement("a");
      link.href = file;
      link.download = `ATOM Pro - Income Tax (PAN) Bank Account Details ${formattedDateTime}.xlsx`;
      link.click();
      snack.success("Income Tax (PAN) Bank Account Details exported successfully");
    },
    onError: (err: any) => {
      snack.error(err);
      snack.error(handleError(err));
    },
  });
  const handleExport = () => {
    if (isExporting) return;
    mutate1({
      search: search,
      limit: pageCount,
      offset: page * pageCount,
      ...filters,
    });
  };

  const handleSort = (v: any) => {
    sortHandler({
      key: v.key,
      columns,
      sortState: sort,
      setSortState: setSort,
      setColumns: setColumns,
    });
  };


  const handleSearch = (e: any) => {
    setSearch(e);
    setPage(0);
  };

  const {
    categories,
  } = useClientCategories({});
  const clientCategoryOptions =
    useMemo(() => {

      return categories.reduce(
        (acc: any, item: any) => {

          acc[String(item.id)] =
            item.name;

          return acc;

        },
        {},
      );

    }, [categories]);

  const filterConfig = useMemo(() => ({

    accountType: {
      label: "Account Type",
      type: dropDownType.object,
      options: accountTypeMap,
    },
    status: {
      label: "Account Status",
      type: dropDownType.object,
      options: accountStatusMap,
    },
    clientCategory: {
      label: "Category",
      type: dropDownType.object,
      options: clientCategoryOptions,
    },
  }), [
    clientCategoryOptions,
  ]);

  const chips: any = buildChips(filters, filterConfig);

  const isFilterActive = hasActiveFilters(filters);

  return (
    <Box m={1}>
      <Box pr={2} pl={2} sx={{ display: "flex", justifyContent: 'space-between' }}>

        <SearchContainer
          minWidth="330px"
          value={search}
          debounced
          placeHolder=" Search by Assessee Name | PAN"
          onChange={handleSearch}
        />
        <Box display='flex' alignItems='center'>
          <Box>
            <FilterEngine
              config={filterConfig}
              filters={filters}
              setFilters={setFilters}
              setPage={setPage}
            />
          </Box>

          {isFilterActive && (
            <Tooltip title={'Reset Filters'}>
              <img
                src={clearFilters}
                alt="Export"
                style={{ cursor: 'pointer', width: 38, height: 38, marginRight: "5px", marginLeft: "5px" }}
                onClick={() => {
                  setFilters({
                    accountType: null,
                    status: null,
                    clientCategory: null,
                  });
                  setSearch("");
                }}
              />
            </Tooltip>
          )}

          <Tooltip title={isExporting ? 'Exporting...' : 'Data Export to Excel'}>
            <img
              src={excelIcon}
              alt="Export"
              style={{
                cursor: isExporting ? 'not-allowed' : 'pointer',
                width: 33,
                height: 33,
                marginRight: "20px",
                marginTop: "3px",
                opacity: isExporting ? 0.5 : 1
              }}
              onClick={isExporting ? undefined : handleExport}
            />
          </Tooltip>
        </Box>

      </Box>
      <Box>
        <Stack direction="row" spacing={1} sx={{ mb: 1, ml: 2, mt: 1, flexWrap: "wrap" }}>
          {chips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              onDelete={() => {
                const updated = {
                  ...filters,
                  [chip.key]: null,
                };
                setFilters(updated);
              }}
              variant="outlined"
              color="primary"
            />
          ))}
        </Stack>
      </Box>

      <Box mt={1}>
        <HorizontalTable
          sx={{ pb: 0, mb: 0, height: "530px" }}
          pagination={{ totalCount, pageCount, setPageCount, page, setPage }}
          data={data?.data.result || []}
          columns={columns}
          sortHandler={handleSort}
          loading={isLoading}
        />
      </Box>
      {/* NEW: Remarks popup */}
      <Dialog
        open={remarksDialog.open}
        onClose={() => setRemarksDialog({ open: false, text: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remarks</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {remarksDialog.text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemarksDialog({ open: false, text: "" })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IncomeTaxBankAccDeatils;
